import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { ArrowLeft, MailCheck, Ship } from "lucide-react";

export function AuthShell({ children, title, subtitle }) {
  const { t } = useI18n();
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="p-4 sm:p-6 flex items-center justify-between">
        <Link to="/" data-testid="auth-logo" className="flex items-center gap-2 font-heading font-extrabold text-xl text-primary">
          <span className="grid h-8 w-8 place-items-center bg-accent text-white"><Ship className="h-4 w-4" /></span> Colis2Go
        </Link>
        <Link to="/" data-testid="back-home" className="text-sm text-muted-foreground hover:text-accent flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" /> {t("auth.backHome")}
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <h1 className="font-heading font-extrabold text-2xl tracking-tight">{title}</h1>
          {subtitle && <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>}
          <div className="mt-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function OtpBoxes({ value, onChange, testid = "otp" }) {
  const refs = useRef([]);
  const digits = (value + "      ").slice(0, 6).split("");
  const set = (i, d) => {
    const arr = value.split("");
    arr[i] = d;
    onChange(arr.join("").replace(/\s/g, "").slice(0, 6));
    if (d && i < 5) refs.current[i + 1]?.focus();
  };
  return (
    <div className="flex gap-2 justify-between">
      {digits.map((d, i) => (
        <input key={i} ref={(el) => (refs.current[i] = el)} data-testid={`${testid}-${i}`}
          inputMode="numeric" maxLength={1} value={d.trim()}
          onChange={(e) => set(i, e.target.value.replace(/\D/g, ""))}
          onKeyDown={(e) => { if (e.key === "Backspace" && !d.trim() && i > 0) refs.current[i - 1]?.focus(); }}
          onPaste={(e) => { e.preventDefault(); onChange(e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6)); }}
          className="h-12 w-11 border border-border text-center text-xl font-bold focus:border-accent focus:outline-none bg-white" />
      ))}
    </div>
  );
}

export function useResendTimer(initial = 0) {
  const [left, setLeft] = useState(initial);
  useEffect(() => {
    if (left <= 0) return;
    const t = setInterval(() => setLeft((x) => x - 1), 1000);
    return () => clearInterval(t);
  }, [left]);
  return [left, setLeft];
}

export default function VerifyEmail() {
  const { t } = useI18n();
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const email = user?.email || params.get("email") || "";
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [editEmail, setEditEmail] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [cooldown, setCooldown] = useResendTimer(60);

  const confirm = async () => {
    if (code.length !== 6) { toast.error(t("otp.enter6")); return; }
    setBusy(true);
    try {
      await api.post("/auth/verify-email/confirm", { email, code });
      toast.success(t("otp.verified"));
      await checkAuth();
      navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || t("otp.invalid"));
    } finally { setBusy(false); }
  };

  const resend = async () => {
    try {
      await api.post("/auth/verify-email/send", { email });
      toast.success(t("otp.resent"));
      setCooldown(60); setCode("");
    } catch (e) { toast.error(e.response?.data?.detail || t("otp.wait")); }
  };

  const changeEmail = async () => {
    try {
      await api.post("/auth/verify-email/update", { new_email: newEmail });
      toast.success(`${t("otp.sentTo")} ${newEmail}`);
      await checkAuth();
      setEditEmail(false); setCode(""); setCooldown(60);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <AuthShell title={t("otp.verifyTitle")}
      subtitle={<>{t("otp.sub1")} <strong>{email}</strong>{t("otp.sub2")}</>}>
      <div className="space-y-5">
        <OtpBoxes value={code} onChange={setCode} testid="verify-otp" />
        <Button data-testid="verify-submit" disabled={busy} onClick={confirm}
          className="w-full h-11 rounded-sm bg-accent hover:bg-accent/90 text-white font-bold gap-2">
          <MailCheck className="h-4 w-4" /> {t("otp.verifyBtn")}
        </Button>
        <div className="flex items-center justify-between text-sm">
          <button data-testid="verify-resend" onClick={resend} disabled={cooldown > 0}
            className="text-accent font-semibold disabled:text-muted-foreground">
            {cooldown > 0 ? `${t("otp.resend")} (${cooldown}s)` : t("otp.resend")}
          </button>
          {user && !user.email_verified && (
            <button data-testid="verify-edit-email" onClick={() => setEditEmail(!editEmail)} className="text-muted-foreground hover:text-accent">
              {t("otp.editEmail")}
            </button>
          )}
        </div>
        {editEmail && (
          <div className="border border-border p-3 space-y-2">
            <Label>{t("otp.newEmail")}</Label>
            <Input data-testid="verify-new-email" type="email" className="rounded-sm" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
            <Button data-testid="verify-new-email-submit" size="sm" className="rounded-sm bg-primary text-white" onClick={changeEmail}>{t("otp.updateResend")}</Button>
          </div>
        )}
        <p className="text-xs text-muted-foreground text-center">
          {t("otp.already")} <Link to="/login" className="text-accent font-semibold">{t("auth.loginBtn")}</Link>
        </p>
      </div>
    </AuthShell>
  );
}
